import { useState, useEffect } from 'react';
import { 
  FileText, CheckCircle, Clock, AlertCircle, Download, 
  Search, Filter, Eye, Send, Users, ChevronDown,
  Calendar, Shield, Loader2, X
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Document Viewer Modal
const DocumentViewerModal = ({ document, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0D0D0D] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">{document.title}</h2>
            <p className="text-white/50 text-sm">
              Signed on {new Date(document.signed_at).toLocaleDateString()}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 p-3">
                <p className="text-white/50 text-xs uppercase mb-1">Printed Name</p>
                <p className="text-white">{document.printed_name}</p>
              </div>
              <div className="bg-white/5 p-3">
                <p className="text-white/50 text-xs uppercase mb-1">IP Address</p>
                <p className="text-white font-mono text-sm">{document.ip_address}</p>
              </div>
              <div className="bg-white/5 p-3">
                <p className="text-white/50 text-xs uppercase mb-1">Document ID</p>
                <p className="text-white font-mono text-sm">{document.document_id}</p>
              </div>
              <div className="bg-white/5 p-3">
                <p className="text-white/50 text-xs uppercase mb-1">Category</p>
                <p className="text-white capitalize">{document.category}</p>
              </div>
            </div>
            
            {/* Signature Preview */}
            <div className="border border-white/10 p-4">
              <p className="text-white/50 text-xs uppercase mb-2">Signature</p>
              <div className="bg-[#0A0A0A] p-4 text-center">
                <p className="text-[#D4AF37] text-2xl font-signature" style={{ fontFamily: "'Dancing Script', cursive" }}>
                  {document.printed_name}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            Close
          </button>
          <button
            className="px-4 py-2 bg-[#D4AF37] text-black font-bold hover:bg-[#F5D47B] transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

// User Documents Panel
const UserDocumentsPanel = ({ userId, userEmail, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await axios.get(`${API}/documents/admin/user/${userId}`);
        setDocuments(response.data);
      } catch (err) {
        console.error('Failed to fetch documents:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [userId]);

  const handleResend = async () => {
    try {
      await axios.post(`${API}/documents/admin/resend/${userId}`);
      alert('Signature request sent!');
    } catch (err) {
      console.error('Failed to resend:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="bg-[#0D0D0D] border border-white/10">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white">Signed Documents</h3>
          <p className="text-white/50 text-sm">{userEmail}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleResend}
            className="px-3 py-1.5 bg-white/10 text-white text-sm hover:bg-white/20 transition-colors flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            Resend Request
          </button>
          <button onClick={onClose} className="p-2 hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>
      </div>

      {/* Documents List */}
      <div className="p-4 space-y-2 max-h-[400px] overflow-y-auto">
        {documents.length === 0 ? (
          <p className="text-white/50 text-center py-8">No signed documents yet</p>
        ) : (
          documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{doc.title}</p>
                  <p className="text-white/50 text-xs">
                    Signed {new Date(doc.signed_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewingDoc(doc)}
                  className="p-2 hover:bg-white/10 transition-colors"
                  title="View Document"
                >
                  <Eye className="w-4 h-4 text-white/60" />
                </button>
                <button
                  className="p-2 hover:bg-white/10 transition-colors"
                  title="Download PDF"
                >
                  <Download className="w-4 h-4 text-white/60" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Viewing Modal */}
      {viewingDoc && (
        <DocumentViewerModal document={viewingDoc} onClose={() => setViewingDoc(null)} />
      )}
    </div>
  );
};

// Main Admin Document Center Component
const AdminDocumentCenter = () => {
  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [completedUsers, setCompletedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'completed'

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pendingRes, completedRes] = await Promise.all([
          axios.get(`${API}/documents/admin/pending`),
          axios.get(`${API}/documents/admin/completed`)
        ]);
        setPendingUsers(pendingRes.data);
        setCompletedUsers(completedRes.data);
      } catch (err) {
        console.error('Failed to fetch document data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const activeList = activeTab === 'pending' ? pendingUsers : completedUsers;
  const filteredUsers = activeList.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#D4AF37]" />
            Document Center
          </h2>
          <p className="text-white/50">Manage e-signatures and document compliance</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-white/50 text-sm">Pending Signatures</p>
              <p className="text-2xl font-bold text-white">{pendingUsers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-white/50 text-sm">Fully Compliant</p>
              <p className="text-2xl font-bold text-white">{completedUsers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-white/50 text-sm">Total Documents</p>
              <p className="text-2xl font-bold text-white">19</p>
            </div>
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-white/50 text-sm">Active Clients</p>
              <p className="text-2xl font-bold text-white">{pendingUsers.length + completedUsers.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <input
            type="text"
            placeholder="Search by email or company name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#121212] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-3 bg-[#121212] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
        >
          <option value="all">All Categories</option>
          <option value="build">Build</option>
          <option value="protect">Protect</option>
          <option value="grow">Grow</option>
        </select>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10" data-testid="admin-doc-tabs">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-5 py-3 text-sm font-medium uppercase tracking-wider transition-colors border-b-2 ${
            activeTab === 'pending'
              ? 'text-[#D4AF37] border-[#D4AF37]'
              : 'text-white/50 border-transparent hover:text-white/80'
          }`}
          data-testid="tab-pending"
        >
          Pending ({pendingUsers.length})
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`px-5 py-3 text-sm font-medium uppercase tracking-wider transition-colors border-b-2 ${
            activeTab === 'completed'
              ? 'text-green-500 border-green-500'
              : 'text-white/50 border-transparent hover:text-white/80'
          }`}
          data-testid="tab-completed"
        >
          Completed ({completedUsers.length})
        </button>
      </div>

      {/* Signatures Table */}
      <div className="bg-[#121212] border border-white/10">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">
            {activeTab === 'pending' ? 'Pending Signatures' : 'Completed Onboarding Vaults'}
          </h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center">
            {activeTab === 'pending' ? (
              <>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-white/60">All clients have completed their documents!</p>
              </>
            ) : (
              <>
                <FileText className="w-12 h-12 text-white/30 mx-auto mb-4" />
                <p className="text-white/60">No clients have completed onboarding yet.</p>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-white/50 text-sm font-medium">Client</th>
                <th className="text-left p-4 text-white/50 text-sm font-medium">Company</th>
                <th className="text-left p-4 text-white/50 text-sm font-medium">
                  {activeTab === 'pending' ? 'Progress' : 'Completed'}
                </th>
                <th className="text-right p-4 text-white/50 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <p className="text-white">{user.email}</p>
                  </td>
                  <td className="p-4">
                    <p className="text-white/70">{user.company_name || '—'}</p>
                  </td>
                  <td className="p-4">
                    {activeTab === 'pending' ? (
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-2 bg-white/10 overflow-hidden">
                          <div 
                            className="h-full bg-[#D4AF37]"
                            style={{ width: `${user.completion_percentage}%` }}
                          />
                        </div>
                        <span className="text-white/70 text-sm">
                          {user.signed}/{user.required}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-white/70 text-sm">
                          {user.completed_at ? new Date(user.completed_at).toLocaleDateString() : '—'} · {user.signed} docs
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="px-3 py-1.5 bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
                        data-testid={`view-docs-${user.user_id}`}
                      >
                        View Vault
                      </button>
                      {activeTab === 'pending' && (
                        <button
                          className="px-3 py-1.5 bg-[#D4AF37] text-black text-sm font-bold hover:bg-[#F5D47B] transition-colors"
                        >
                          Send Reminder
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Selected User Panel */}
      {selectedUser && (
        <UserDocumentsPanel
          userId={selectedUser.user_id}
          userEmail={selectedUser.email}
          onClose={() => setSelectedUser(null)}
        />
      )}

      {/* Document Templates Reference */}
      <div className="bg-[#121212] border border-white/10">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">Document Templates</h3>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Global */}
          <div className="border border-white/10 p-4">
            <h4 className="text-[#D4AF37] font-bold mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Global (All Clients)
            </h4>
            <ul className="space-y-2 text-sm">
              <li className="text-white/70">• Carrier Profile Form</li>
              <li className="text-white/70">• Non-Broker Disclaimer</li>
              <li className="text-white/70">• Terms & Conditions</li>
              <li className="text-white/70">• Privacy Policy</li>
              <li className="text-white/70">• Refund & Cancellation</li>
              <li className="text-white/70">• W-9 Collection</li>
            </ul>
          </div>
          
          {/* Build */}
          <div className="border border-white/10 p-4">
            <h4 className="text-green-400 font-bold mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Build Documents
            </h4>
            <ul className="space-y-2 text-sm">
              <li className="text-white/70">• BYB Service Agreement</li>
              <li className="text-white/70">• Startup Acknowledgment</li>
              <li className="text-white/70">• Timeline & Responsibility</li>
              <li className="text-white/70">• Independent Contractor</li>
              <li className="text-white/70">• Communication Consent</li>
            </ul>
          </div>
          
          {/* Protect */}
          <div className="border border-white/10 p-4">
            <h4 className="text-blue-400 font-bold mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Protect Documents
            </h4>
            <ul className="space-y-2 text-sm">
              <li className="text-white/70">• Compliance Agreement</li>
              <li className="text-white/70">• Limited Power of Attorney</li>
              <li className="text-white/70">• Document Handling Auth</li>
            </ul>
          </div>
          
          {/* Grow */}
          <div className="border border-white/10 p-4">
            <h4 className="text-purple-400 font-bold mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Grow Documents
            </h4>
            <ul className="space-y-2 text-sm">
              <li className="text-white/70">• Carrier-Dispatcher Agreement</li>
              <li className="text-white/70">• Dispatch Tier Addendum</li>
              <li className="text-white/70">• Payment Authorization</li>
              <li className="text-white/70">• Rate Confirmation Ack</li>
              <li className="text-white/70">• Load Commitment Agreement</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDocumentCenter;
export { UserDocumentsPanel, DocumentViewerModal };
